import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, TableCell, TableRow } from '@mui/material';
import { flexRender } from '@tanstack/react-table';
import { type CSSProperties, useMemo } from 'react';
import classSet from 'react-classset';

import type { ManualOrderMenuState, ManualOrderMoveAction } from './manualOrder.utils';
import ManualOrderHandle from './ManualOrderHandle';

type Props = {
  row: any;
  selectedRowId?: string;
  showRowSelection?: boolean;
  rowSelection?: Record<string, boolean>;
  handleRowSelect?: (rowId: string) => void;
  sortable?: boolean;
  enableRowDrag?: boolean;
  dragHandleOnly?: boolean;
  hideDragHandles?: boolean;
  manualOrderEnabled?: boolean;
  manualOrderMenuState?: ManualOrderMenuState;
  onManualOrderMove?: (action: ManualOrderMoveAction, rowId: string) => void;
  rowRefs?: any;
  tableId?: string;
  externalDND?: boolean;
};

const VNTableRow = ({
  row,
  selectedRowId = '',
  showRowSelection = false,
  rowSelection = {},
  handleRowSelect,
  sortable,
  enableRowDrag,
  dragHandleOnly = false,
  hideDragHandles = false,
  manualOrderEnabled = false,
  manualOrderMenuState,
  onManualOrderMove,
  rowRefs,
  tableId,
  externalDND = false,
  ...rest
}: Props) => {
  const originalId = row.original.id ?? row.id;
  const uniqueId = externalDND ? `${tableId}-${originalId}` : originalId;

  const { transform, transition, setNodeRef, setActivatorNodeRef, isDragging, attributes, listeners } = useSortable({
    id: uniqueId,
    data: {
      tableId,
      row: row.original,
      originalId,
    },
  });

  const showSortCell = Boolean(sortable) || externalDND;
  const isDraggable = enableRowDrag ?? (showSortCell && !hideDragHandles);
  const useHandleActivator = isDraggable && dragHandleOnly;

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 9999 : 1,
    position: isDragging ? 'relative' : 'static',
    cursor: isDraggable && !useHandleActivator ? (isDragging ? 'grabbing' : 'grab') : undefined,
    userSelect: isDraggable ? 'none' : undefined,
    ...rest.style,
  };

  const isSelected = useMemo(() => row.id === selectedRowId || row.original?.id === selectedRowId, [selectedRowId, row]);
  const renderRowStyle = (tableRow) =>
    classSet({
      'fuel-table-row': true,
      'fuel-table-row--disabled': tableRow.original.isDeleted && tableRow.original.entityType === 'Clip',
      'fuel-table-row--expired': tableRow.original.cssClassForExpired,
      'fuel-table-row--embargoed': tableRow.original.cssClassForEmbargoed,
      'fuel-table-row--isGlobalInterstitialsChannel': tableRow.original.isGlobalInterstitialsChannel,
      'fuel-table-row--parent': tableRow.original.isParentChannel,
      'fuel-table-row--nested': tableRow.depth > 0,
      'hover-row': true,
      highlight: isSelected,
    });

  const defaultMenuState: ManualOrderMenuState = {
    canMoveTop: false,
    canMoveUp: false,
    canMoveDown: false,
    canMoveBottom: false,
  };

  return (
    <TableRow
      key={row.id}
      className={renderRowStyle(row)}
      style={style}
      ref={(el) => {
        setNodeRef(el);
        if (isDraggable && !useHandleActivator) setActivatorNodeRef(el);
        if (rowRefs && rowRefs.current && row?.original?.id) {
          rowRefs.current[row.original.id] = el;
        }
      }}
      {...(isDraggable && !useHandleActivator ? { ...attributes, ...listeners } : {})}
      {...rest}
    >
      {showSortCell ? (
        <TableCell className={classSet({ 'sort-cell': true, 'sort-cell--manual-order': manualOrderEnabled })}>
          {isDraggable ? (
            manualOrderEnabled ? (
              <ManualOrderHandle
                isDragging={isDragging}
                setActivatorNodeRef={setActivatorNodeRef}
                attributes={attributes}
                listeners={listeners}
                menuState={manualOrderMenuState ?? defaultMenuState}
                onMove={(action) => onManualOrderMove?.(action, String(originalId))}
              />
            ) : (
              <span
                className="sort-cell-handle"
                ref={useHandleActivator ? setActivatorNodeRef : undefined}
                {...(useHandleActivator ? { ...attributes, ...listeners } : {})}
                style={{
                  cursor: useHandleActivator ? (isDragging ? 'grabbing' : 'grab') : undefined,
                }}
              >
                <FontAwesomeIcon icon={faBars} />
              </span>
            )
          ) : null}
        </TableCell>
      ) : null}

      {showRowSelection && (
        <TableCell className="checkbox-cell" onPointerDown={(e) => e.stopPropagation()}>
          <Checkbox size="small" checked={!!rowSelection[row.id]} onChange={() => handleRowSelect(row.id)} />
        </TableCell>
      )}

      {row.getVisibleCells().map((cell: any) => (
        <TableCell
          key={cell.id}
          sx={{
            textAlign: cell.column.columnDef.meta?.style?.textAlign || 'center',
            width: cell.column.columnDef.size || 'auto',
            minWidth: cell.column.columnDef.size || 'auto',
            maxWidth: cell.column.columnDef.size || 'auto',
            color: row.original.cssClassForEmbargoed ? '#d9534f !important' : 'inherit',
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
};

export default VNTableRow;
